import { GoalImpactLevel } from './goal-impact-level';
import { ID } from './id';
import {
  GoalGroupBy,
  GoalSortingMode,
  GroupByMode,
  SortingMode,
  TaskGroupBy,
} from './sorting-mode';
import { TaskPriorityOptions, TaskType } from './task-base';
import { Timestamp } from './timestamp';

export enum DocumentOrganizationKeys {
  SortingMode = 'sortingMode',
  GroupByMode = 'groupByMode',
  Filters = 'filters',
  CollapsedSections = 'collapsedSections',
  ShowCompleted = 'showCompleted',
  Or = 'or',
}

export enum FilterOptionKeys {
  LifeArea = 'lifeArea',
  Priority = 'priority',
  Type = 'type',
  Tag = 'tag',
  Duration = 'duration',
  Goal = 'goal',
  GoalActive = 'goalActive',
  Date = 'date',
  Deadline = 'deadline',
  GoalImpact = 'goalImpact',
  CompletedAt = 'completedAt',
  Deleted = 'deleted',
}

export enum FilterDocumentDurationTypes {
  Minutes = 'minutes',
  Hours = 'hours',
}

export type FilterDocumentFilter<TypeToSave extends string> = {
  include?: TypeToSave[] | null;
  noTag?: boolean | null;
};

export enum FilterDocumentTagMatchMode {
  Any = 'any',
  All = 'all',
  NoTag = 'no-tag',
}

export type FilterDocumentTagFilter = FilterDocumentFilter<ID> & {
  matchMode?: FilterDocumentTagMatchMode | null;
};

export enum FilterDocumentDateFilterTypes {
  Today = 'today',
  ThisWeek = 'thisWeek',
  NextWeek = 'nextWeek',
  ThisMonth = 'thisMonth',
  NextMonth = 'nextMonth',
  ThisQuarter = 'thisQuarter',
  NextQuarter = 'nextQuarter',
  ThisYear = 'thisYear',
  NextYear = 'nextYear',
  Overdue = 'overdue',
  Next14Days = 'next14Days',
  Custom = 'custom',
  NoDeadline = 'noDeadline',
  HasDeadline = 'hasDeadline',
}

export enum FilterDocumentDateCustomTypes {
  Before = 'before',
  After = 'after',
  Between = 'between',
}

export type FilterDocumentDurationFilter = {
  more?: { type: FilterDocumentDurationTypes; value: number } | null;
  less?: { type: FilterDocumentDurationTypes; value: number } | null;
};

export type FilterDocumentDateFilter = {
  type: FilterDocumentDateFilterTypes;
  custom: {
    type: FilterDocumentDateCustomTypes;
    start: Timestamp | null;
    end: Timestamp | null;
  } | null;
};

export type FilterDocumentFilters = {
  [FilterOptionKeys.LifeArea]?: FilterDocumentFilter<ID> | null;
  [FilterOptionKeys.Priority]?: FilterDocumentFilter<TaskPriorityOptions> | null;
  [FilterOptionKeys.Type]?: FilterDocumentFilter<TaskType> | null;
  [FilterOptionKeys.Tag]?: FilterDocumentTagFilter | null;
  [FilterOptionKeys.Goal]?:
    | (FilterDocumentFilter<ID> & { availableOptions?: ID[] | null })
    | null;
  [FilterOptionKeys.GoalActive]?: FilterDocumentDateFilter | null;
  [FilterOptionKeys.Duration]?: FilterDocumentDurationFilter | null;
  [FilterOptionKeys.Date]?: FilterDocumentDateFilter | null;
  [FilterOptionKeys.Deadline]?: FilterDocumentDateFilter | null;
  [FilterOptionKeys.GoalImpact]?: FilterDocumentFilter<GoalImpactLevel> | null;
  [FilterOptionKeys.CompletedAt]?: FilterDocumentDateFilter | null;
  [FilterOptionKeys.Deleted]?: boolean | null;
};

export type FilterDocument<
  GBM extends GroupByMode = TaskGroupBy,
  SM extends SortingMode | GoalSortingMode = SortingMode,
  F extends FilterOptionKeys = FilterOptionKeys,
> = {
  [DocumentOrganizationKeys.SortingMode]?: SM | null;
  [DocumentOrganizationKeys.GroupByMode]?: GBM | null;
  [DocumentOrganizationKeys.CollapsedSections]?: ID[] | null;
  [DocumentOrganizationKeys.Filters]?: Pick<FilterDocumentFilters, F> | null;
  [DocumentOrganizationKeys.ShowCompleted]?: boolean | null;
  [DocumentOrganizationKeys.Or]?: Pick<FilterDocumentFilters, F>[] | null;
};

export type GoalFilterDocument = FilterDocument<
  GoalGroupBy,
  GoalSortingMode,
  | FilterOptionKeys.LifeArea
  | FilterOptionKeys.Goal
  | FilterOptionKeys.Date
  | FilterOptionKeys.Deadline
  | FilterOptionKeys.GoalImpact
>;
